/**
 * Application shell — light, capability-aware, responsive.
 *
 * Navigation is derived from the capability list the server returned for this
 * user, so the sidebar cannot offer a link the backend would refuse. Hiding a
 * forbidden action beats rendering a button that 403s.
 *
 * IA is organised by job, not by route history:
 *   INSPECT  — the press and its truth (operator, quality)
 *   ANALYZE  — process and fleet intelligence (engineer, plant)
 *   MANAGE   — configuration and governance (admin)
 *   OPERATE  — marketplace operations (Sensifai admin only)
 */
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Me } from '../api/types';
import { AdvisoryBanner } from './Advisory';

interface NavItem {
  to: string;
  label: string;
  capability?: string;
  section: string;
  icon: ReactNode;
}

const I = {
  station: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  quality: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 3l7 4v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V7l7-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  root: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="5" r="2.5" />
      <circle cx="5" cy="19" r="2.5" />
      <circle cx="19" cy="19" r="2.5" />
      <path d="M12 7.5v4m0 0l-5.5 5m5.5-5l5.5 5" />
    </svg>
  ),
  spc: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 19h16" />
      <path d="M5 15l3-4 3 2 4-6 4 5" />
    </svg>
  ),
  plant: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 21V9l6 4V9l6 4V5h6v16H3z" />
    </svg>
  ),
  roi: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 20l5-6 4 3 7-9" />
      <path d="M15 8h5v5" />
    </svg>
  ),
  recipes: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M6 3h9l4 4v14H6V3z" />
      <path d="M9 11h7M9 15h7" />
    </svg>
  ),
  fleet: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  sub: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
    </svg>
  ),
  audit: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M8 3h8v4H8z" />
      <path d="M16 5h3v16H5V5h3" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  ),
  system: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 20a8 8 0 1 1 8-8" />
      <path d="M12 12l4-4" />
      <path d="M20 16v4h-4" />
    </svg>
  ),
  ops: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c3 3 3 15 0 18-3-3-3-15 0-18z" />
    </svg>
  ),
};

const NAV: NavItem[] = [
  { to: '/station', label: 'My station', section: 'Inspect', capability: 'station:view', icon: I.station },
  { to: '/quality', label: 'Quality', section: 'Inspect', capability: 'quality:metrics:view', icon: I.quality },
  { to: '/engineer', label: 'Root cause', section: 'Inspect', capability: 'process:rootcause:view', icon: I.root },
  { to: '/quality/spc', label: 'SPC & capability', section: 'Analyze', capability: 'quality:metrics:view', icon: I.spc },
  { to: '/plant', label: 'Plant overview', section: 'Analyze', capability: 'fleet:oee:view', icon: I.plant },
  { to: '/plant/roi', label: 'ROI', section: 'Analyze', capability: 'fleet:roi:view', icon: I.roi },
  { to: '/admin/recipes', label: 'Recipes', section: 'Manage', capability: 'station:view', icon: I.recipes },
  { to: '/admin', label: 'Fleet & inventory', section: 'Manage', capability: 'org:inventory:manage', icon: I.fleet },
  { to: '/admin/subscription', label: 'Subscription', section: 'Manage', capability: 'org:subscription:view', icon: I.sub },
  { to: '/admin/audit', label: 'Audit trail', section: 'Manage', capability: 'quality:audit:view', icon: I.audit },
  { to: '/admin/system', label: 'System health', section: 'Manage', capability: 'org:inventory:manage', icon: I.system },
  { to: '/ops', label: 'Marketplace ops', section: 'Operate', capability: 'ops:view', icon: I.ops },
];

const ROLE_LABEL: Record<string, string> = {
  OPERATOR: 'Operator',
  PROCESS_ENGINEER: 'Process engineer',
  QUALITY_MANAGER: 'Quality manager',
  PLANT_MANAGER: 'Plant manager',
  ORG_ADMIN: 'Organisation admin',
  SENSIFAI_ADMIN: 'Sensifai admin',
};

export function Shell({
  me,
  title,
  subtitle,
  live,
  children,
}: {
  me: Me;
  title: string;
  subtitle?: string;
  live?: boolean;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const caps = new Set(me.capabilities);
  const visible = NAV.filter((n) => !n.capability || caps.has(n.capability));
  const sections = [...new Set(visible.map((n) => n.section))];

  const nav = (
    <nav className="nav" aria-label="Primary" onClick={() => setOpen(false)}>
      {sections.map((section) => (
        <div key={section}>
          <div className="nav-section">{section}</div>
          {visible
            .filter((n) => n.section === section)
            .map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
              >
                <span className="nav-ico">{n.icon}</span>
                {n.label}
              </NavLink>
            ))}
        </div>
      ))}
      {visible.length === 0 && (
        <div className="empty">
          <strong>No permitted views</strong>
          This role has no dashboard capabilities yet.
        </div>
      )}
    </nav>
  );

  return (
    <div className="shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>

      {open && <div className="drawer-backdrop" onClick={() => setOpen(false)} />}

      <aside className={`sidebar${open ? ' open' : ''}`} aria-label="Sidebar">
        <div className="brand">
          <div className="brand-name">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="#0a5fae" strokeWidth="2" />
              <circle cx="12" cy="12" r="3.5" fill="#0a5fae" />
            </svg>
            PlasticVision
          </div>
          <div className="brand-sub">Advisory inspection</div>
        </div>

        {nav}

        <div className="sidebar-foot">
          <div style={{ color: 'var(--ink-2)', fontWeight: 600, marginBottom: 1 }}>
            {me.displayName ?? me.email ?? 'Signed in'}
          </div>
          <div>{ROLE_LABEL[me.role] ?? me.role}</div>
          <button
            className="btn sm"
            style={{ marginTop: 10, width: '100%' }}
            onClick={() => {
              window.location.href = '/logout';
            }}
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <button
            className="btn sm menu-btn"
            aria-label="Open navigation"
            onClick={() => setOpen(true)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <div>
            <h1>{title}</h1>
            {subtitle && <div className="topbar-sub">{subtitle}</div>}
          </div>
          <div className="topbar-spacer" />
          {live !== undefined && (
            <span className={`pill ${live ? 'ok' : 'warn'}`}>
              {live ? 'Live' : 'Reconnecting'}
            </span>
          )}
          <span className="pill muted">{ROLE_LABEL[me.role] ?? me.role}</span>
          <button className="btn sm ghost" onClick={() => navigate(me.defaultSurface)}>
            Home
          </button>
        </header>

        <main className="content" id="main-content">
          <AdvisoryBanner compact />
          {children}
        </main>
      </div>
    </div>
  );
}
